import React from 'react';
import PropTypes from 'prop-types';
import { addDays, isEqual, isValid } from 'date-fns';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { Dropdown, DropdownPropTypes } from 'components/Dropdown';
import { Dropdown as Dropdown2, DropdownToggle } from 'components/Dropdown2';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { isTodayTimezone, parseISOWithTimezone } from 'helpers/intl-fns';

import DateControlsStepper from './DateControlsStepper';
import DateControlsPicker from './DateControlsPicker';
import messages from './DateControls.messages';
import './DateControls.scss';

const { block, element } = bem('date-controls');

const DateControls = ({
  attachment = 'left',
  date,
  isDesktop,
  setDate,
  disabled,
  defaultDate = new Date().toISOString(),
  isControl,
  hasError,
  label,
  useDefaultLabel = true,
  timeZone,
  isValidDate,
  ...rest
}) => {
  date = date instanceof Date ? date : parseISOWithTimezone(date, timeZone);
  defaultDate = defaultDate instanceof Date ? defaultDate : parseISOWithTimezone(defaultDate, timeZone);

  if (!isValid(date)) {
    date = defaultDate;
  }

  function adjustDate(event, amount, closeMenu) {
    event.stopPropagation();
    const nextDate = addDays(date, amount);
    // We check for date validation when updating date through arrow controls.
    if (isValidDate) {
      if (isValidDate(nextDate)) {
        setDate(nextDate);
      }
    } else {
      setDate(nextDate);
    }
    closeMenu();
  }

  const isActive = !isTodayTimezone(date, timeZone) && !isEqual(date, defaultDate);

  let arrowTheme;
  if (disabled) {
    arrowTheme = 'disabled';
  } else if (isActive) {
    arrowTheme = 'brand';
  } else {
    arrowTheme = 'dark';
  }

  if (!isControl) {
    return (
      <Dropdown
        attachment={attachment}
        Toggle={DateControlsStepper}
        Content={DateControlsPicker}
        date={date}
        setDate={setDate}
        disabled={disabled}
        shouldCloseContentOnChange={false}
        defaultDate={defaultDate}
        isDesktop={isDesktop}
        timeZone={timeZone}
        isValidDate={isValidDate}
        {...rest}
      />
    );
  }

  return (
    <Dropdown2 attachment={attachment} closeOnSelect={isDesktop} disabled={disabled}>
      {({ closeMenu }) => (
        <>
          <DropdownToggle
            label={label || (useDefaultLabel && messages.label)}
            isActive={isActive}
            hasError={hasError}
            hideCaret>
            <div className={block({ isControl })}>
              <span data-cypress="date-controls-current-day">
                {isTodayTimezone(date, timeZone) ? (
                  <Message message={messages.today} values={{ date }} />
                ) : (
                  <Message format="medium-w-dow-no-year" value={date} />
                )}
              </span>
              <div className={element('buttons')}>
                <button
                  className={element('icon-wrap', { isControl })}
                  type="button"
                  onClick={event => adjustDate(event, -1, closeMenu)}
                  disabled={disabled}
                  tabIndex="-1"
                  data-cypress="date-controls-previous-day">
                  <SvgArrow className="date-controls__previous-day" direction="left" theme={arrowTheme} />
                </button>
                <button
                  className={element('icon-wrap', { isControl })}
                  type="button"
                  onClick={event => adjustDate(event, 1, closeMenu)}
                  disabled={disabled}
                  tabIndex="-1"
                  data-cypress="date-controls-next-day">
                  <SvgArrow className="date-controls__next-day" direction="right" theme={arrowTheme} />
                </button>
              </div>
            </div>
          </DropdownToggle>
          <DateControlsPicker
            date={date}
            setDate={setDate}
            closeMenu={closeMenu}
            isDesktop={isDesktop}
            isValidDate={isValidDate}
            {...rest}
          />
        </>
      )}
    </Dropdown2>
  );
};

DateControls.propTypes = {
  attachment: DropdownPropTypes.attachment,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  defaultDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  isControl: PropTypes.bool,
  isDesktop: PropTypes.bool,
  label: PropTypes.oneOfType([intlMessage, PropTypes.oneOf([false])]),
  useDefaultLabel: PropTypes.bool,
  setDate: PropTypes.func,
  timeZone: PropTypes.string,
  isValidDate: PropTypes.func,
};

export default DateControls;
